import { type WithdrawalClient } from '@funkit/connect-core'
import { LIGHTER_FAST_MAX_WITHDRAWAL_USD } from '@funkit/connect-core/clients/lighter'
import { createLighterAdaptedWallet } from '@funkit/connect-core/clients/lighter'
import { LIGHTER_CHAIN_ID } from '@funkit/chains'
import type { Address } from 'viem'

import type { WithdrawalFlowConfig } from '../../src/flow/withdrawalFlowConfig'
import { logger } from '../../src/logging/logger'
import type { LighterWithdrawalFlowConfig } from './lighterWithdrawalConfig'

/**
 * Lighter USDC Perps, addressed as relay does: the zero address maps to relay
 * asset '0' via `RELAY_LIGHTER_FUN_ADDRESS_MAP` in fun-relay.
 */
const LIGHTER_USDC_PERPS_ADDRESS =
  '0x0000000000000000000000000000000000000000' as Address

/** Same id relay calls `RELAY_LIGHTER_CHAIN_ID`; the config carries it as a string. */
const LIGHTER_SOURCE_CHAIN_ID = `${LIGHTER_CHAIN_ID}`

/** Fast is USDC-only, so web's USD floor is also its token-unit floor. */
const LIGHTER_FAST_MIN_WITHDRAWAL_USD = 4

/**
 * Wraps the host's signer so `onBeforeSign` is awaited before the first
 * transfer. Lighter strategies and withdrawals share one L2 signing account, so
 * an in-flight strategy would bump the nonce out from under a Fast withdrawal
 * exactly as it would a Secure one. Throwing aborts before any nonce is used.
 */
function withPreSignHook(
  signerClient: LighterWithdrawalFlowConfig['signerClient'],
  onBeforeSign: LighterWithdrawalFlowConfig['onBeforeSign'],
): LighterWithdrawalFlowConfig['signerClient'] {
  if (!onBeforeSign) {
    return signerClient
  }
  return {
    ...signerClient,
    transfer: async (params) => {
      await onBeforeSign()
      return signerClient.transfer(params)
    },
  }
}

/**
 * A {@link WithdrawalClient} for Lighter Fast. Relay drives `adaptedWallet`
 * directly — the EVM members exist to satisfy the interface and to throw
 * loudly if something ever routes an EVM leg through this client, which for
 * Fast would mean signing on the wrong rail.
 *
 * `address()` is the L2 account index, not an EVM address: Lighter addresses
 * accounts by index, and that is what relay and the tracking row record.
 */
export function createLighterFastWithdrawalClient({
  signerClient,
  accountIndex,
  onBeforeSign,
}: {
  signerClient: LighterWithdrawalFlowConfig['signerClient']
  accountIndex: string
  /**
   * Host hook awaited before the first L2 signature. Relay drives the adapter,
   * so the only point Fast controls is the signer itself.
   */
  onBeforeSign?: LighterWithdrawalFlowConfig['onBeforeSign']
  // The adapter is narrowed to Lighter's own wallet: the interface field is
  // relay's per-VM union, and callers of this client know which vm they got.
}): WithdrawalClient & {
  adaptedWallet: ReturnType<typeof createLighterAdaptedWallet>
} {
  // Fail here rather than let a NaN account index reach relay, where it
  // surfaces much later as an unattributable signing failure.
  if (!/^\d+$/.test(accountIndex)) {
    throw new Error(
      `Lighter Fast needs a numeric account index, got: ${accountIndex}`,
    )
  }
  return {
    adaptedWallet: createLighterAdaptedWallet({
      signerClient: withPreSignHook(signerClient, onBeforeSign),
      accountIndex: Number(accountIndex),
      logger,
    }),
    address: () => accountIndex,
    getChainId: async () => LIGHTER_CHAIN_ID,
    // Lighter is single-chain — nothing to switch to.
    switchChain: async () => undefined,
    sendTransaction: async () => {
      throw new Error('Lighter Fast executes through its relay adapter')
    },
    confirmTransaction: async () => {
      throw new Error('Lighter Fast executes through its relay adapter')
    },
  }
}

/**
 * The withdrawal the crypto rail runs for Lighter Fast: USDC Perps out of the
 * user's L2 account, quoted and executed through Fun + Relay to any supported
 * destination. Port of web's `buildLighterFastWalletWithdrawalConfig`.
 *
 * Limits are web's USD numbers expressed in token units. Fast is USDC-only, so
 * the two are the same number; RN has no price feed yet, and a USD limit
 * without one is inert (see `withdrawal/validation.ts`).
 */
export function buildLighterFastWithdrawalConfig({
  config,
  lighter,
  accountIndex,
  isUnifiedTradingMode,
}: {
  /** The host's withdrawal config — title and callbacks carry over. */
  config: WithdrawalFlowConfig
  lighter: LighterWithdrawalFlowConfig
  accountIndex: string
  /** Unified accounts hold one USDC balance, so the label drops "(Perps)". */
  isUnifiedTradingMode: boolean
}): WithdrawalFlowConfig {
  const sourceToken = {
    chainId: LIGHTER_SOURCE_CHAIN_ID,
    address: LIGHTER_USDC_PERPS_ADDRESS,
    symbol: 'USDC',
    decimals: 6,
  }
  return {
    ...config,
    sourceToken,
    sourceTokens: [
      {
        token: sourceToken,
        label: isUnifiedTradingMode ? 'USDC' : 'USDC (Perps)',
      },
    ],
    withdrawalSourceTokenBalance: lighter.withdrawalSourceTokenBalance,
    defaultReceiveToken: 'USDC',
    withdrawalWallet: createLighterFastWithdrawalClient({
      signerClient: lighter.signerClient,
      accountIndex,
      onBeforeSign: lighter.onBeforeSign,
    }),
    // Both in token units, not USD: the price API can't resolve Lighter's
    // source (an L2 balance behind a pseudo-address), and a USD bound with no
    // price silently goes unenforced. Fast is USDC-only, so the numbers match.
    getMinWithdrawalAmount: () => LIGHTER_FAST_MIN_WITHDRAWAL_USD,
    getMaxWithdrawalAmount: () => LIGHTER_FAST_MAX_WITHDRAWAL_USD,
  }
}
